import React, { useState, useEffect } from 'react';

const RateLimitModal = ({ show, onClose, translations, retryAfter, email }) => {
  const [timeRemaining, setTimeRemaining] = useState(retryAfter || 0);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (!show || timeRemaining <= 0) {
      setCanClose(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setCanClose(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [show, timeRemaining]);

  useEffect(() => {
    if (show && retryAfter) {
      setTimeRemaining(retryAfter);
      setCanClose(false);
    }
  }, [show, retryAfter]);

  if (!show) return null;

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '40px 30px',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          textAlign: 'center',
        }}
      >
        {/* Warning Icon */}
        <div
          style={{
            width: '70px',
            height: '70px',
            backgroundColor: '#fef3c7',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <svg
            width='40'
            height='40'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z'
              fill='#d97706'
            />
          </svg>
        </div>

        {/* Title */}
        <h4
          style={{
            marginBottom: '15px',
            color: '#1f2937',
            fontWeight: '600',
            fontSize: '20px',
          }}
        >
          {translations?.tooManyAttempts || 'Too Many Attempts'}
        </h4>

        {/* Message */}
        <p
          style={{
            color: '#6b7280',
            marginBottom: '20px',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
        >
          {translations?.rateLimitMessage ||
            'You have made too many requests. Please wait before trying again.'}
        </p>

        {/* Email Display */}
        {email && (
          <p
            style={{
              color: '#374151',
              marginBottom: '20px',
              fontSize: '13px',
              wordBreak: 'break-all',
              backgroundColor: '#f3f4f6',
              padding: '10px',
              borderRadius: '6px',
            }}
          >
            <strong>Email:</strong> {email}
          </p>
        )}

        {/* Countdown Timer */}
        <div
          style={{
            backgroundColor: '#fee2e2',
            border: '2px solid #fca5a5',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '25px',
          }}
        >
          <p
            style={{
              color: '#7f1d1d',
              marginBottom: '10px',
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            {translations?.retryIn || 'Please try again in:'}
          </p>
          <div
            style={{
              fontSize: '36px',
              fontWeight: 'bold',
              color: '#dc2626',
              fontFamily: 'monospace',
              letterSpacing: '2px',
            }}
          >
            {formattedTime}
          </div>
        </div>

        {/* Info Text */}
        <p
          style={{
            color: '#9ca3af',
            fontSize: '12px',
            marginBottom: '20px',
          }}
        >
          {translations?.rateLimitInfo ||
            'This is a security measure to protect your account. The modal will automatically close when the timer reaches zero.'}
        </p>

        {/* Close Button - Disabled until timer expires */}
        <button
          onClick={onClose}
          disabled={!canClose}
          style={{
            width: '100%',
            padding: '12px 20px',
            backgroundColor: canClose ? '#2563eb' : '#d1d5db',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: canClose ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            opacity: canClose ? 1 : 0.6,
          }}
          onMouseEnter={(e) => {
            if (canClose) {
              e.target.style.backgroundColor = '#1d4ed8';
            }
          }}
          onMouseLeave={(e) => {
            if (canClose) {
              e.target.style.backgroundColor = '#2563eb';
            }
          }}
        >
          {canClose
            ? translations?.close || 'Close'
            : `${translations?.waitingForTimer || 'Waiting'} (${formattedTime})`}
        </button>
      </div>
    </div>
  );
};

export default RateLimitModal;
